import { useReactiveVar } from '@apollo/client'
import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Skeleton from '@material-ui/lab/Skeleton'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import clsx from 'clsx'
import { ReplacementOption } from 'components/WorkspaceEditor/components/NodeContents'
import React, { useCallback, useMemo } from 'react'
import { DESIGNATION_MAX_LENGTH, SERVER_DESIGNATION_PLACEHOLDER } from '../constants'
import { useDrawingDesignationHandlers } from '../hooks'
import { parseDesignationText } from '../services/textParser'
import { useDesignationFieldStyles } from './useDesignationFieldStyles'

// Persists designation values across virtualization unmount/remount cycles
const designationCache = new Map<string, string>()

/** Clears the designation cache - exported for testing */
export const clearDesignationCache = () => designationCache.clear()

/** Clears specific element from cache - call when PT content is removed from project */
export const clearDesignationCacheForElement = (baseElementId: string) => {
    designationCache.delete(baseElementId)
}

interface DesignationFieldProps {
    keyedText: string
    keyedTextValue?: string
    contentItemId: string
    editable: boolean
    inactive: boolean
    showPrefix?: boolean
    showSuffix?: boolean
    isArticle?: boolean
    /** Raw drawing designation from the server — avoids lossy text parsing for PT names with parentheses */
    serverDesignation?: string
}

/**
 * Product Type designation field with inline editing.
 * Format: "{prefix} ({designation}): {value}"
 * Example: "A. Clear Glass (DD-001): ASTM C1036"
 */
export const DesignationField = ({
    keyedText,
    keyedTextValue,
    contentItemId,
    editable,
    inactive,
    showPrefix = true,
    showSuffix = true,
    isArticle = false,
    serverDesignation,
}: DesignationFieldProps) => {
    const classes = useDesignationFieldStyles()
    const { isReviewer } = useReactiveVar(userInfo)
    const textPrefix = useMemo(
        () => parseDesignationText(keyedText, { serverDesignation }).prefix,
        [keyedText, serverDesignation]
    )
    const resolvedDesignation = serverDesignation || ''

    // Cache invalidation moved here (from render-time logic) so it only runs
    // when the user explicitly saves/removes a designation, not during every render.
    // This prevents the cache from being cleared during virtualization remount cycles
    // when parsedDesignation is temporarily empty.
    const handleDesignationCache = useCallback(
        (baseElementId: string, designation: string) => {
            if (baseElementId) {
                if (designation) {
                    designationCache.set(baseElementId, designation)
                } else {
                    designationCache.delete(baseElementId)
                }
            }
        },
        []
    )

    const { handleValueChange, isSaving, productFamilyId, productTypeElementId, baseElementId } = useDrawingDesignationHandlers({
        currentDesignation: resolvedDesignation,
        contentItemId,
        onDesignationSave: handleDesignationCache,
    })

    // Content not fully added when either ID is empty - show strikethrough and disable editing
    const isContentNotAddedToPT = !productTypeElementId || !productFamilyId
    const isFieldInactive = inactive || isContentNotAddedToPT

    // Cache management keyed by baseElementId (stable across add/remove cycles).
    if (baseElementId) {
        if (resolvedDesignation) {
            designationCache.set(baseElementId, resolvedDesignation)
        } else if (serverDesignation === '') {
            designationCache.delete(baseElementId)
        }
    }
    const designationValue = resolvedDesignation || (baseElementId && designationCache.get(baseElementId)) || ''

    const designationContentItem: Partial<TextContentElement> = useMemo(
        () => ({
            id: `${contentItemId}-drawing-designation`,
            text: designationValue,
            placeholderText: SERVER_DESIGNATION_PLACEHOLDER,
            type: 'ReplacementOption',
        }),
        [contentItemId, designationValue]
    )

    return (
        <>
            {showPrefix && (
                <span data-testid="designation-prefix" className={isFieldInactive ? classes.inactiveText : undefined}>{textPrefix}</span>
            )}
            <span className={classes.container} data-testid="designation-input-container">
                <span className={clsx(classes.content, isSaving ? classes.contentHidden : classes.contentVisible)}>
                    <ReplacementOption
                        contentItem={designationContentItem}
                        editable={editable && !isSaving}
                        inactive={isFieldInactive}
                        disabled={isReviewer || isContentNotAddedToPT}
                        onValueChange={handleValueChange}
                        component="span"
                        maxLength={DESIGNATION_MAX_LENGTH}
                        allowEmpty
                    />
                </span>
                {isSaving && (
                    <Skeleton
                        variant="rect"
                        animation="wave"
                        className={classes.skeleton}
                    />
                )}
            </span>
            {showSuffix && (
                <span data-testid="designation-suffix">:{keyedTextValue ?? ''}</span>
            )}
        </>
    )
}
